import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import Tooltip from '../../../../../shared/components/widgets/Tooltip';
import style, { DROPDOWN_COLORS } from '../../../../../shared/components/themeSupport';
import './GoogleConstantMultiSelect.scss';

/**
 * GoogleConstantMultiSelect
 *
 * Reusable searchable multi-select for Google Ads "constant" reference data
 * (Language Constants today, Geo Target Constants for Countries/States/Cities/
 * Postal Codes later). The backend only needs the underlying resource_name
 * keys (e.g. "languageConstants/1000"), but users should see and search by
 * human-readable names (e.g. "English").
 *
 * The redux-form field value stays a plain comma-separated string of keys
 * (unchanged format, e.g. "languageConstants/1000,languageConstants/1003"),
 * so no backend/serialization changes are required. This component only
 * changes how that value is displayed and edited.
 *
 * - On mount, resolves any existing keys in the field to display labels via
 *   `lookupByResourceNamesFn` (the "convert keys to display names" trick).
 * - Search-as-you-type calls `searchFn(searchTerm)` (server-side fuzzy search).
 * - Selected items render as removable chips (react-select's native multi-value
 *   pills, each with an "x") rather than as comma-delimited text.
 * - On change, re-serializes the selected values back into a comma-separated
 *   string, sorted by value (not selection order), and writes it back via
 *   input.onChange.
 *
 * Pass `singleSelect` to restrict the field to exactly one selection (the
 * stored value is then a single key, still a plain string).
 *
 * For very large reference sets (e.g. Cities, Postal Codes - tens of thousands
 * of rows), pass `minSearchLength` (e.g. 2 or 3) to disable browsing the full
 * unfiltered list and require the user to type before any options are shown/
 * fetched. This turns the field into a pure "search and add" experience
 * instead of a "dropdown of all options" the UI can't reasonably render.
 */
const GoogleConstantMultiSelect = ({
  input,
  meta,
  label,
  tooltip,
  placeholder,
  searchFn,
  lookupByResourceNamesFn,
  extraSearchParams,
  minSearchLength,
  singleSelect,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isResolvingInitialValue, setIsResolvingInitialValue] = useState(true);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const hasResolvedInitialValue = useRef(false);

  // On mount, resolve the initial CSV string of keys into display labels.
  useEffect(() => {
    const resolveInitialValue = async () => {
      if (hasResolvedInitialValue.current) return;
      hasResolvedInitialValue.current = true;

      const existingKeys = (input.value || '')
        .split(',')
        .map(key => key.trim())
        .filter(key => key);

      if (existingKeys.length === 0) {
        setIsResolvingInitialValue(false);
        return;
      }

      const resolvedOptions = await lookupByResourceNamesFn({
        resourceNames: existingKeys,
        jwtToken: user.jwtToken,
        ...extraSearchParams,
      });

      // Fall back to the raw key as the label for any key that wasn't returned
      // by the lookup (e.g. a deprecated/unknown constant), so nothing is lost.
      const resolvedValues = new Set(resolvedOptions.map(option => option.value));
      const fallbackOptions = existingKeys
        .filter(key => !resolvedValues.has(key))
        .map(key => ({ label: key, value: key }));

      setSelectedOptions([...resolvedOptions, ...fallbackOptions]);
      setIsResolvingInitialValue(false);
    };

    resolveInitialValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = (searchTerm, callback) => {
    // For large reference sets, require a minimum number of typed characters
    // before hitting the search API or showing any options at all - avoids
    // ever trying to render/browse the full (huge) unfiltered list.
    if (minSearchLength > 0 && searchTerm.trim().length < minSearchLength) {
      callback([]);
      return;
    }

    searchFn({ searchTerm, jwtToken: user.jwtToken, ...extraSearchParams })
      .then(options => callback(options))
      .catch(() => callback([]));
  };

  const noOptionsMessage = ({ inputValue }) => {
    if (minSearchLength > 0 && inputValue.trim().length < minSearchLength) {
      return `Type at least ${minSearchLength} characters to search...`;
    }
    return 'No results found';
  };

  const handleChange = (newSelectedOptions) => {
    // In single-select mode react-select passes one option (or null), not an array
    let options = newSelectedOptions || [];
    if (singleSelect) {
      options = newSelectedOptions ? [newSelectedOptions] : [];
    }
    const sortedOptions = [...options].sort((a, b) => a.value.localeCompare(b.value));
    setSelectedOptions(sortedOptions);
    input.onChange(sortedOptions.map(option => option.value).join(','));
  };

  const themeColors = style(theme, DROPDOWN_COLORS);
  const textColor = theme.className.endsWith('light') ? '#000000' : '#FFFFFF';

  return (
    <div className="form__form-group">
      <span className="form__form-group-label">
        {label}{' '}
        {tooltip && <Tooltip id={`${input.name}-tooltip`} msg={tooltip} placement="top" />}
      </span>
      <div className="form__form-group-field">
        <div className="google-constant-multi-select" style={{ width: '100%' }}>
          <AsyncSelect
            isMulti={!singleSelect}
            isClearable
            cacheOptions
            defaultOptions={minSearchLength === 0}
            isLoading={isResolvingInitialValue}
            value={singleSelect ? (selectedOptions[0] || null) : selectedOptions}
            loadOptions={loadOptions}
            onChange={handleChange}
            noOptionsMessage={noOptionsMessage}
            placeholder={placeholder}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            theme={selectTheme => ({
              ...selectTheme,
              colors: {
                ...selectTheme.colors,
                primary: `${themeColors.colorPrimary}`,
                neutral0: `${themeColors.colorBackground}`,
                neutral80: textColor,
              },
            })}
            classNamePrefix="react-select"
          />
        </div>
      </div>
      {meta?.touched && meta.error && (
        <span className="form__form-group-error" style={{ display: 'block', width: '100%' }}>{meta.error}</span>
      )}
    </div>
  );
};

GoogleConstantMultiSelect.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  placeholder: PropTypes.string,
  searchFn: PropTypes.func.isRequired,
  lookupByResourceNamesFn: PropTypes.func.isRequired,
  extraSearchParams: PropTypes.shape(),
  minSearchLength: PropTypes.number,
  singleSelect: PropTypes.bool,
};

GoogleConstantMultiSelect.defaultProps = {
  meta: null,
  tooltip: null,
  placeholder: 'Search...',
  extraSearchParams: {},
  minSearchLength: 0,
  singleSelect: false,
};

export default GoogleConstantMultiSelect;
